import React from 'react';
import { chakra } from '@chakra-ui/react';

// =============================================================================
// OMNI AppHeader Select Component
// =============================================================================
// Universal select component using OMNI AppHeader recipe for consistent
// country/agency selector styling with proper OMNI design system compliance
// =============================================================================

// Country/Agency types
export interface SelectOption {
  value: string;
  label?: string;
}

// OMNI Header Select
// =============================================================================
// Country/Agency selector with OMNI styling and interaction states
export interface OmniHeaderSelectProps
  extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    'size' | 'onChange'
  > {
  variant?: 'default' | 'outline' | 'filled' | 'contrast';
  size?: 'sm' | 'md' | 'lg';
  placeholder?: string;
  value?: string;
  disabled?: boolean;
  options: SelectOption[];
  onChange?: (value: string) => void;
}

// Create OMNI Header Select using factory pattern
const OmniHeaderSelectBase = chakra('select', {
  base: {
    bg: 'omni.bg.surface',
    color: 'omni.text.primary',
    border: '1px solid',
    borderColor: 'omni.border.subtle',
    borderRadius: 'omni.radius.medium',
    px: 3,
    py: 2,
    fontSize: { base: 'xs', sm: 'sm', md: 'sm' },
    minW: { base: '120px', sm: '140px', md: '150px' },
    maxW: { base: '140px', sm: '160px', md: '180px' },
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 8px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '16px',
    paddingRight: '32px',
    _hover: {
      borderColor: 'omni.border.hover',
      bg: 'omni.bg.hover.subtle',
    },
    _focus: {
      borderColor: 'omni.border.focus',
      boxShadow: '0 0 0 1px var(--omni-border-focus)',
      outline: 'none',
    },
    _disabled: {
      bg: 'omni.bg.disabled',
      color: 'omni.text.disabled',
      borderColor: 'omni.border.disabled',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
  variants: {
    variant: {
      default: {
        // Ensure option text is readable for default variant
        '& option': {
          bg: 'white',
          color: 'gray.900',
          padding: '8px 12px',
        },
        '& option:checked': {
          bg: 'brand.500',
          color: 'white',
        },
        '& option:hover': {
          bg: 'brand.50',
          color: 'gray.900',
        },
      },
      outline: {
        bg: 'transparent',
        // Ensure option text is readable for outline variant
        '& option': {
          bg: 'white',
          color: 'gray.900',
          padding: '8px 12px',
        },
        '& option:checked': {
          bg: 'brand.500',
          color: 'white',
        },
        '& option:hover': {
          bg: 'brand.50',
          color: 'gray.900',
        },
        _hover: {
          bg: 'omni.bg.hover.subtle',
        },
      },
      filled: {
        bg: 'omni.bg.muted',
        borderColor: 'transparent',
        // Ensure option text is readable for filled variant
        '& option': {
          bg: 'white',
          color: 'gray.900',
          padding: '8px 12px',
        },
        '& option:checked': {
          bg: 'brand.500',
          color: 'white',
        },
        '& option:hover': {
          bg: 'brand.50',
          color: 'gray.900',
        },
        _hover: {
          bg: 'omni.bg.hover.muted',
        },
      },
      contrast: {
        // High contrast variant for selects without background
        bg: 'rgba(255, 255, 255, 0.95)',
        color: 'gray.900',
        border: '1px solid',
        borderColor: 'gray.300',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        // Ensure option text is readable
        '& option': {
          bg: 'white',
          color: 'gray.900',
          padding: '8px 12px',
        },
        '& option:checked': {
          bg: 'brand.500',
          color: 'white',
        },
        '& option:hover': {
          bg: 'brand.50',
          color: 'gray.900',
        },
        _hover: {
          bg: 'white',
          borderColor: 'brand.300',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
        },
        _focus: {
          bg: 'white',
          borderColor: 'brand.500',
          boxShadow: '0 0 0 2px rgba(0, 161, 210, 0.2)',
          outline: 'none',
        },
        _disabled: {
          bg: 'rgba(255, 255, 255, 0.7)',
          color: 'gray.400',
          borderColor: 'gray.200',
        },
      },
    },
    size: {
      sm: {
        px: 2,
        py: 1,
        fontSize: '2xs',
        minW: { base: '100px', sm: '120px' },
        maxW: { base: '120px', sm: '140px' },
      },
      md: {
        px: 3,
        py: 2,
        fontSize: { base: 'xs', sm: 'sm' },
        minW: { base: '120px', sm: '140px', md: '150px' },
        maxW: { base: '140px', sm: '160px', md: '180px' },
      },
      lg: {
        px: 4,
        py: 3,
        fontSize: { base: 'sm', sm: 'md' },
        minW: { base: '140px', sm: '160px', md: '180px' },
        maxW: { base: '160px', sm: '180px', md: '200px' },
      },
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

export const OmniHeaderSelect = React.forwardRef<
  HTMLSelectElement,
  OmniHeaderSelectProps
>((props, ref) => {
  const {
    variant = 'default',
    size = 'md',
    placeholder = 'Select...',
    value = '',
    disabled = false,
    options = [],
    onChange,
    ...rest
  } = props;

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  return (
    <OmniHeaderSelectBase
      ref={ref}
      variant={variant}
      size={size}
      value={value}
      disabled={disabled}
      onChange={handleChange}
      className="omni-select"
      data-omni-select="true"
      {...rest}
    >
      {placeholder && (
        <option value="" disabled={!value}>
          {placeholder}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label || option.value}
        </option>
      ))}
    </OmniHeaderSelectBase>
  );
});

OmniHeaderSelect.displayName = 'OmniHeaderSelect';

// =============================================================================
// EXPORTS
// =============================================================================
// All component types are already exported with their declarations above
